import { styled } from "@linaria/react";
import { XIcon } from "@phosphor-icons/react";
import { match } from "ts-pattern";

import Button, { ButtonSize, ButtonVariant } from "@gx/dls/buttons/Button";
import ProgressBar, { ProgressBarVariant } from "@gx/dls/metrics/ProgressBar";
import Text, { TextSize, TextVariant } from "@gx/dls/text/Text";
import { withTheme, type PropsWithTheme } from "@gx/dls/theme";
import { SourceSyncOperationStatus } from "@gx/ts-contracts/services/database/models/public_pb";

import type { FileUploadProgress } from "@/pages/sources/components/create/sources/types";
import { DROPZONE_TABLE_VIEW_COLUMNS } from "@/pages/sources/components/dropzone/constants";
import DropzoneWidgetTableCell from "@/pages/sources/components/dropzone/DropzoneWidgetTableCell";
import { DropzoneTableViewColumn } from "@/pages/sources/components/dropzone/types";
import { formatFileSize } from "@/pages/sources/components/dropzone/utils";

const DropzoneWidgetTableRowWrapper = withTheme(styled.div<PropsWithTheme>`
  display: flex;
  align-items: center;
  width: 100%;
  border-bottom: 0.5px solid ${({ theme }) => theme.color.border.primary};
  padding: 8px;

  &:last-child {
    border-bottom: none;
  }
`);

export interface DropzoneWidgetTableRowProps {
  index: number;
  file: File;
  onRemoveFile: (index: number) => void;
  isDisabled?: boolean;
  progress?: FileUploadProgress;
}

const DropzoneWidgetTableRow = ({
  index,
  file,
  onRemoveFile,
  isDisabled = false,
  progress,
}: DropzoneWidgetTableRowProps) => {
  const handleRemove = () => {
    onRemoveFile(index);
  };

  const renderActionColumn = (columnIndex: number) => {
    // Show progress bar when uploading or completed
    if (progress) {
      const isComplete = progress.status === SourceSyncOperationStatus.COMPLETED;
      const isFailed = progress.status === SourceSyncOperationStatus.FAILED;

      const variant = isComplete
        ? ProgressBarVariant.SUCCESS
        : isFailed
          ? ProgressBarVariant.ERROR
          : ProgressBarVariant.PRIMARY;

      return (
        <DropzoneWidgetTableCell column={DropzoneTableViewColumn.FILE_ACTIONS} key={columnIndex}>
          <ProgressBar variant={variant} percentage={isComplete ? 100 : progress.percentage} />
        </DropzoneWidgetTableCell>
      );
    }

    // Show remove button when not uploading
    return (
      <DropzoneWidgetTableCell column={DropzoneTableViewColumn.FILE_ACTIONS} key={columnIndex}>
        <Button
          label="Remove"
          icon={XIcon}
          variant={ButtonVariant.SECONDARY}
          size={ButtonSize.SMALL}
          onClick={handleRemove}
          isDisabled={isDisabled}
        />
      </DropzoneWidgetTableCell>
    );
  };

  const renderColumn = (column: DropzoneTableViewColumn, columnIndex: number) => {
    return match(column)
      .with(DropzoneTableViewColumn.FILE_NAME, () => {
        return (
          <DropzoneWidgetTableCell column={column} key={columnIndex}>
            <Text size={TextSize.BODY_SM} isEllipsis>
              {file.name}
            </Text>
          </DropzoneWidgetTableCell>
        );
      })
      .with(DropzoneTableViewColumn.FILE_SIZE, () => {
        return (
          <DropzoneWidgetTableCell column={column} key={columnIndex}>
            <Text variant={TextVariant.TERTIARY} size={TextSize.BODY_SM} isEllipsis>
              {formatFileSize(file.size)}
            </Text>
          </DropzoneWidgetTableCell>
        );
      })
      .with(DropzoneTableViewColumn.FILE_ACTIONS, () => {
        return renderActionColumn(columnIndex);
      })
      .exhaustive();
  };

  return (
    <DropzoneWidgetTableRowWrapper>
      {DROPZONE_TABLE_VIEW_COLUMNS.map((column, columnIndex) => renderColumn(column, columnIndex))}
    </DropzoneWidgetTableRowWrapper>
  );
};

export default DropzoneWidgetTableRow;
